import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  CircularProgress, 
  Drawer,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Paper,
  Checkbox,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { format } from 'date-fns';

// Components
import StatusChip from '../components/ui/StatusChip';
import { useAuth } from '../contexts/AuthContext';
import '../styles/shared.css';

const Apply = () => {
  const { user } = useAuth();
  const [elections, setElections] = useState([]);
  const [userElection, setUserElection] = useState(null);
  const [userNomination, setUserNomination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bio, setBio] = useState('');
  const [bioError, setBioError] = useState('');
  const [acceptedStatement, setAcceptedStatement] = useState(false);
  const [statementError, setStatementError] = useState('');

  // State for election selection when multiple elections are available
  const [selectedElectionId, setSelectedElectionId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all elections
        const electionsRes = await axios.get('/api/elections/');
        setElections(electionsRes.data);
        
        // If there's only one active election, select it automatically
        const activeElections = electionsRes.data.filter(e => isElectionActive(e));
        if (activeElections.length === 1) {
          setUserElection(activeElections[0]);
          setSelectedElectionId(activeElections[0].id);
        } else if (activeElections.length > 0) {
          // Default to first active election but user can select
          setUserElection(activeElections[0]);
          setSelectedElectionId(activeElections[0].id);
        }
        
        // Check if user already has a nomination in any election
        if (electionsRes.data.length > 0) {
          for (const election of electionsRes.data) {
            try {
              const nominationsRes = await axios.get(`/api/nominations/?election_id=${election.id}`);
              const userNom = nominationsRes.data.find(n => n.candidate_id === user.id);
              if (userNom) {
                setUserNomination(userNom);
                setUserElection(election);
                setSelectedElectionId(election.id);
                break;
              }
            } catch (nomErr) {
              console.error('Failed to fetch nominations:', nomErr);
            }
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        // Don't show error message, just set loading to false
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user]);
  
  // Check if an election is currently active (application period ongoing)
  const isElectionActive = (election) => {
    if (!election) return false;
    
    const now = new Date();
    const applicationStart = new Date(election.nomination_start_date);
    const applicationEnd = new Date(election.nomination_end_date);
    
    return now >= applicationStart && now <= applicationEnd;
  };
  
  // Handle election selection change
  const handleElectionChange = (event) => {
    const electionId = event.target.value;
    setSelectedElectionId(electionId);
    
    const selected = elections.find(e => e.id === electionId);
    setUserElection(selected);
    
    // Check if user has nomination for this election
    const checkNomination = async () => {
      try {
        const nominationsRes = await axios.get(`/api/nominations/?election_id=${electionId}`);
        const userNom = nominationsRes.data.find(n => n.candidate_id === user.id);
        setUserNomination(userNom || null);
      } catch (err) {
        console.error('Failed to fetch nominations:', err);
        setUserNomination(null);
      }
    };
    
    checkNomination();
  };
  
  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    setBioError('');
    setStatementError('');
    
    // Validate bio
    if (bio.trim().length < 10) {
      setBioError('Bio must be at least 10 characters long');
      return;
    }
    
    // Validate statement acceptance
    if (!acceptedStatement) {
      setStatementError('You must accept the candidate statement to proceed');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Get the token from localStorage
      const token = localStorage.getItem('accessToken');
      
      // Make sure we have the token
      if (!token) {
        setError('You need to be logged in to submit a application');
        return;
      }
      
      // Include authorization header explicitly
      await axios.post('/api/nominations/', 
        {
          election_id: userElection.id,
          bio: bio
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      setSuccess('Your application has been submitted successfully and is pending approval!');
      setDrawerOpen(false);
      
      // Refresh application data
      const nominationsRes = await axios.get(`/api/nominations/?election_id=${userElection.id}`);
      const userNom = nominationsRes.data.find(n => n.candidate_id === user.id);
      setUserNomination(userNom);
      
    } catch (err) {
      console.error('Application submission error:', err);
      setError(err.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const checkNominationEligibility = () => {
    if (!userElection) return false;
    
    // First check if application period is active
    const now = new Date();
    const applicationStart = new Date(userElection.nomination_start_date);
    const applicationEnd = new Date(userElection.nomination_end_date);
    const isNominationPeriodOpen = now >= applicationStart && now <= applicationEnd;
    
    // Then check if user has been at the company for at least a year
    const hasRequiredTenure = checkUserTenure();
    
    return isNominationPeriodOpen && hasRequiredTenure;
  };
  
  // Check if the user has been at the company for at least a year
  const checkUserTenure = () => {
    if (!user.joining_date) return false;
    
    const now = new Date();
    const joinDate = new Date(user.joining_date);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    return joinDate <= oneYearAgo;
  };
  
  const getNominationStatus = () => {
    if (!userElection) {
      return {
        status: "unavailable",
        message: "No active election is available."
      };
    }
    
    if (userNomination) {
      return {
        status: userNomination.status,
        message: `Your application is ${userNomination.status}.`
      };
    }
    
    const now = new Date();
    const applicationStart = new Date(userElection.nomination_start_date + 'Z'); // Treat as UTC
    const applicationEnd = new Date(userElection.nomination_end_date + 'Z'); // Treat as UTC
    
    // Check employment tenure
    if (!checkUserTenure()) {
      return {
        status: "ineligible",
        message: "You must be employed for at least 12 months to be eligible as a candidate."
      };
    }
    
    if (now < applicationStart) {
      return {
        status: "upcoming",
        message: `Applications open on ${format(applicationStart, 'MMM d, yyyy')}`
      };
    } else if (now > applicationEnd) {
      return {
        status: "closed",
        message: "The application period has ended."
      };
    } else {
      return {
        status: "open",
        message: "Applications are open! You can submit your candidature now."
      };
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
        <CircularProgress sx={{ color: '#009999' }} />
      </Box>
    );
  }

  const nominationStatus = getNominationStatus();
  const isEligible = checkNominationEligibility();

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h3" component="h1" sx={{ mb: 4, fontWeight: 600 }}>
          Application
        </Typography>
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </motion.div>
        )}
        
        {success && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <Paper
        component={motion.div}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        elevation={4}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: '14px',
          background: 'rgba(127, 140, 156, 0.7)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {user.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.joining_date && `Joined ${format(new Date(user.joining_date), 'MMM d, yyyy')}`}
            </Typography>
          </Box>
          {nominationStatus.status !== 'rejected' && <StatusChip status={nominationStatus.status} />}
        </Box>

        {/* Election Selection Dropdown */}
        {elections.length > 0 && (
          <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
            <InputLabel>Select Election</InputLabel>
            <Select
              value={selectedElectionId || ''}
              onChange={handleElectionChange}
              label="Select Election"
            >
              {elections.map(election => (
                <MenuItem key={election.id} value={election.id}>
                  {election.name} 
                  {isElectionActive(election) && ' (Active)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        

      
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
          <Box>
            <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
              Election Information
            </Typography>
            {nominationStatus.status !== 'rejected' && (
  <StatusChip 
    status={nominationStatus.status} 
    label={nominationStatus.status === "unavailable" ? "Not Available" : (
      nominationStatus.status.charAt(0).toUpperCase() + nominationStatus.status.slice(1)
    )} 
  />
)}
          </Box>
          
          {userElection && (
            <Box sx={{ mt: { xs: 2, sm: 0 } }}>
              <Typography variant="body2" sx={{ mb: 1, textAlign: 'right' }}>
                Application period
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {format(new Date(userElection.nomination_start_date), 'MMM d, yyyy')} - {format(new Date(userElection.nomination_end_date), 'MMM d, yyyy')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Display business units for this election */}
        <Typography variant="body1" sx={{ mb: 4 }}>
          {nominationStatus.message}
        </Typography>
        
        {userNomination && (
          <Box sx={{ mt: 2, mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Your Application</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>Bio:</Typography>
            <Paper
              sx={{
                p: 2,
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.1)',
                mb: 2
              }}
            >
              <Typography variant="body1">
                {userNomination.bio}
              </Typography>
            </Paper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Submitted on {format(new Date(userNomination.created_at), 'MMM d, yyyy')}
              </Typography>
              {userNomination.status === 'rejected' && (
  <StatusChip status="error" label="Rejected" />
)}
            </Box>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          {nominationStatus.status !== 'closed' && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={!isEligible || (userNomination && !['rejected', 'withdrawn'].includes(userNomination.status)) || !checkUserTenure()}
              onClick={() => setDrawerOpen(true)}
              title={!checkUserTenure() ? "You must be employed for at least 12 months to nominate yourself" : ""}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '14px',
                fontSize: '1rem',
                boxShadow: '0 4px 20px rgba(112, 41, 99, 0.3)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 6px 25px rgba(112, 41, 99, 0.4)'
                }
              }}
            >
              {userNomination && ['rejected', 'withdrawn'].includes(userNomination.status) ? 'Reapply' : 'Apply'}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Application Form Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '450px' },
            p: 3,
            background: '#708090',
            backgroundImage: 'linear-gradient(135deg, rgba(112, 41, 99, 0.15) 0%, rgba(0, 153, 153, 0.15) 100%)',
          }
        }}
      >
        <Box component="form" onSubmit={handleApplicationSubmit} sx={{ height: '100%' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Typography variant="h4" component="h2" sx={{ mb: 4, color: '#1a202c', fontWeight: 700 }}>
              Submit Your Application
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: '#1a202c !important', fontWeight: 600 }}>Election</InputLabel>
              <Select
                value={userElection?.id || ''}
                label="Election"
                disabled
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1a202c'
                  },
                  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1a202c'
                  },
                  '& .MuiSelect-select': {
                    color: '#1a202c',
                    fontWeight: 600,
                    backgroundColor: 'rgba(112, 41, 99, 0.15)',
                    borderRadius: '10px'
                  },
                  '& .MuiSelect-select.Mui-disabled': {
                    backgroundColor: 'rgba(112, 41, 99, 0.15)',
                    WebkitTextFillColor: '#1a202c'
                  }
                }}
              >
                <MenuItem value={userElection?.id || ''} sx={{ color: '#1a202c' }}>
                  {userElection?.name || 'Selected Election'}
                </MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Short Bio"
              multiline
              rows={6}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              error={!!bioError}
              helperText={bioError || "Tell colleagues a bit about yourself and why you're running for Works Council."}
              sx={{
                mb: 4,
                '& .MuiInputLabel-root': {
                  color: '#1a202c !important',
                  fontWeight: 600
                },
                '& .MuiOutlinedInput-root': {
                  '& input, & textarea': {
                    color: '#1a202c',
                    fontWeight: 500
                  },
                  '& fieldset': {
                    borderColor: '#1a202c'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#702963', 
                    boxShadow: '0 0 0 3px rgba(112, 41, 99, 0.2)'
                  }
                },
                '& .MuiFormHelperText-root': {
                  color: '#4a5568 !important'
                }
              }}
            />
            
            <Box sx={{ mt: 4, mb: 4, px: 1, py: 2, bgcolor: 'rgba(255, 255, 255, 0.09)', borderRadius: 2 }}>              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#1a202c' }}>
                Candidate Statement
              </Typography>
              
              <Typography variant="body2" paragraph sx={{ mb: 2, color: '#2d3748' }}>
                I hereby accept my candidacy for the S[&]T Netherlands Works Council. I have read and agree to the terms set out in the Works Council Regulation document.
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={acceptedStatement}
                    onChange={(e) => setAcceptedStatement(e.target.checked)}
                    sx={{
                      color: statementError ? 'error.main' : 'inherit',
                      '&.Mui-checked': {
                        color: '#702963',
                      },
                    }}
                  />
                }
                label="I accept the above statement"
              />
              {statementError && (
                <FormHelperText error>{statementError}</FormHelperText>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setDrawerOpen(false)}
                disabled={submitting}
                sx={{ borderRadius: '14px' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={submitting}
                sx={{
                  borderRadius: '14px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease'
                }}
              >
                {submitting ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  "Submit Application"
                )}
              </Button>
            </Box>
          </motion.div>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Apply;
